import {
    List,
    Datagrid,
    TextField,
    DateField,
    ReferenceField,
    ShowButton,
    Show,
    SimpleShowLayout,
    Pagination,
    TopToolbar,
    FilterButton,
    ExportButton,
    SearchInput,
    ChipField,
    useRecordContext,
    FunctionField,
} from 'react-admin';

// Custom field to display user information
const UserField = () => {
    const record = useRecordContext();
    if (!record || !record.User) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>Anonymous</span>;
    }
    
    return (
        <span>
            {record.User.name} 
            <br />
            <small style={{ color: '#666' }}>({record.User.email})</small>
        </span>
    );
};

// Custom field to display product information
const ProductField = () => {
    const record = useRecordContext();
    if (!record || !record.product_name) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>None</span>;
    }
    
    return (
        <span>
            {record.product_name}
            {record.product_id && (
                <>
                    <br />
                    <small style={{ color: '#666' }}>Product ID: {record.product_id}</small>
                </>
            )}
        </span>
    );
};

// Custom field to display location information
const LocationField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const parts = [];
    if (record.city && record.city !== 'Local') parts.push(record.city);
    if (record.region && record.region !== 'Local') parts.push(record.region);
    if (record.country && record.country !== 'Local') parts.push(record.country);
    
    if (parts.length === 0) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>Local/Unknown</span>;
    }
    
    return <span>{parts.join(', ')}</span>;
};

// Custom field to style source with color coding
const SourceField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const source = record.source;
    
    // Define source mappings with colors
    const sourceConfig = {
        'email_button': {
            label: 'Email Button',
            color: '#2196f3',
            backgroundColor: '#2196f320'
        },
        'product_detail': {
            label: 'Product Detail',
            color: '#ff9800',
            backgroundColor: '#ff980020'
        },
        'whatsapp_button': {
            label: 'WhatsApp Button',
            color: '#4caf50',
            backgroundColor: '#4caf5020'
        },
        'whatsapp_button_homepage': {
            label: 'WhatsApp Homepage',
            color: '#4caf50',
            backgroundColor: '#4caf5020'
        },
        'whatsapp_product_detail': {
            label: 'WhatsApp Product Detail',
            color: '#25d366',
            backgroundColor: '#25d36620'
        }
    };
    
    // Get config for this source, or default
    const config = sourceConfig[source] || {
        label: source || 'Unknown',
        color: '#757575',
        backgroundColor: '#75757520'
    };
    
    return (
        <span style={{ 
            color: config.color, 
            fontWeight: 'bold',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: config.backgroundColor,
            fontSize: '12px'
        }}>
            {config.label}
        </span>
    );
};

const contactUsLogFilters = [
    <SearchInput source="q" alwaysOn placeholder="Search by user, product, or location..." resettable />,
];

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ExportButton />
    </TopToolbar>
);

export const ContactUsLogList = () => (
    <List 
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        perPage={25}
        sort={{ field: 'created_at', order: 'DESC' }}
        filters={contactUsLogFilters}
        filterDefaultValues={{ q: '' }}
        actions={<ListActions />}
        title="Contact Us Logs"
    >
        <Datagrid>
            <TextField source="id" sortable />
            <SourceField label="Source" sortable={false} />
            <UserField label="User" sortable={false} />
            <ProductField label="Product" sortable={false} />
            <LocationField label="Location" sortable={false} />
            <TextField source="ip_address" label="IP Address" sortable={false} />
            <TextField source="session_id" label="Session" sortable={false} />
            <DateField source="CreatedAt" showTime={true} label="Created At" sortable />
            <ShowButton />
        </Datagrid>
    </List>
);

const ContactUsLogTitle = () => {
    const record = useRecordContext();
    return <span>Contact Us Log {record ? `#${record.id}` : ''}</span>;
};

export const ContactUsLogShow = () => (
    <Show title={<ContactUsLogTitle />}>
        <SimpleShowLayout>
            <TextField source="id" label="ID" />
            <SourceField label="Source" />
            <UserField label="User" />
            <ProductField label="Product" />
            <TextField source="ip_address" label="IP Address" />
            <LocationField label="Location" />
            <TextField source="country" label="Country" />
            <TextField source="region" label="Region/State" />
            <TextField source="city" label="City" />
            <TextField source="latitude" label="Latitude" />
            <TextField source="longitude" label="Longitude" />
            <TextField source="user_agent" label="User Agent" />
            <TextField source="session_id" label="Session ID" />
            <DateField source="CreatedAt" showTime={true}/>
            <DateField source="UpdatedAt" showTime={true}/>
        </SimpleShowLayout>
    </Show>
); 